import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { getToken } from '../../lib/auth'
import { useMe } from '../../hooks/useMe'
import { followProfile, unfollowProfile, getFollowers, getFollowing, type FollowListRow } from '../../api/people'
import { applyFollowToggle, filterFollowRows } from '../../lib/followList'
import FollowUserRow from './FollowUserRow'

type Mode = 'followers' | 'following'

const PAGE_SIZE = 25

export default function FollowListPage({ mode }: { mode: Mode }) {
  const { handle } = useParams()
  const navigate = useNavigate()
  const authed = Boolean(getToken())
  const { me } = useMe(authed)
  const viewerHandle = (me?.profile?.handle || '').replace(/^@/, '').toLowerCase()

  const [rows, setRows] = useState<FollowListRow[]>([])
  const [page, setPage] = useState(1)
  const [count, setCount] = useState(0)
  const [query, setQuery] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [busyIds, setBusyIds] = useState<Record<number, boolean>>({})

  const filtered = useMemo(() => filterFollowRows(rows, query), [rows, query])
  const canLoadMore = rows.length < count
  const title = mode === 'followers' ? 'Followers' : 'Following'
  const fetchList = mode === 'followers' ? getFollowers : getFollowing

  useEffect(() => {
    if (!handle) return
    setRows([])
    setPage(1)
    setCount(0)
    setError(null)
    loadPage(1, true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handle, mode])

  const loadPage = async (nextPage: number, replace = false) => {
    if (!handle) return
    setLoading(true)
    setError(null)
    try {
      const data = await fetchList(handle, nextPage, PAGE_SIZE)
      setCount(data.count || 0)
      setRows((prev) => (replace ? data.results : [...prev, ...data.results]))
      setPage(nextPage)
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Failed to load list')
    } finally {
      setLoading(false)
    }
  }

  const onToggleFollow = async (row: FollowListRow, nextIsFollowing: boolean) => {
    if (!authed) {
      navigate('/login')
      return
    }
    setBusyIds((prev) => ({ ...prev, [row.person_id]: true }))
    const previous = row
    setRows((prev) =>
      prev.map((r) => (r.person_id === row.person_id ? applyFollowToggle(r, nextIsFollowing) : r))
    )
    try {
      const res = nextIsFollowing ? await followProfile(row.public_id) : await unfollowProfile(row.public_id)
      setRows((prev) =>
        prev.map((r) => {
          if (r.person_id !== row.person_id) return r
          const is_followed_by_viewer = res.is_following
          return {
            ...r,
            is_followed_by_viewer,
            followers_count: res.followers_count,
            is_mutual: Boolean(is_followed_by_viewer && r.is_following_viewer),
          }
        })
      )
    } catch (e: any) {
      setRows((prev) => prev.map((r) => (r.person_id === row.person_id ? previous : r)))
      alert(e?.response?.data?.detail || e?.message || 'Follow action failed')
    } finally {
      setBusyIds((prev) => ({ ...prev, [row.person_id]: false }))
    }
  }

  if (!handle) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-10 text-white">
        <div className="text-lg font-semibold">Profile not found.</div>
      </div>
    )
  }

  return (
    <div className="mx-auto max-w-4xl px-4 py-8 text-white">
      <div className="mb-4 flex items-center gap-3">
        <button onClick={() => navigate(-1)} className="rounded-lg border border-white/10 px-3 py-1.5 text-sm hover:bg-white/5">
          Back
        </button>
        <div className="text-xl font-extrabold">{title}</div>
        <div className="text-sm text-white/60">@{handle}</div>
      </div>

      <div className="mb-4 flex items-center gap-2">
        <button
          className={`rounded-full px-3 py-1.5 text-sm ${mode === 'followers' ? 'bg-white/10 text-white' : 'text-white/70 hover:text-white'}`}
          onClick={() => navigate(`/p/${handle}/followers`)}
        >
          Followers
        </button>
        <button
          className={`rounded-full px-3 py-1.5 text-sm ${mode === 'following' ? 'bg-white/10 text-white' : 'text-white/70 hover:text-white'}`}
          onClick={() => navigate(`/p/${handle}/following`)}
        >
          Following
        </button>
      </div>

      <div className="mb-6">
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search"
          className="w-full rounded-xl border border-white/10 bg-white/5 px-4 py-2 text-sm text-white placeholder:text-white/40 outline-none focus:border-white/30"
        />
      </div>

      {error && (
        <div className="mb-4 rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-200">
          {error}
        </div>
      )}

      {loading && rows.length === 0 && (
        <div className="space-y-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="h-24 rounded-2xl border border-white/10 bg-white/5 animate-pulse" />
          ))}
        </div>
      )}

      {!loading && filtered.length === 0 && (
        <div className="rounded-2xl border border-white/10 bg-white/5 px-6 py-8 text-center text-white/70">
          {mode === 'followers' ? 'No followers yet.' : 'Not following anyone yet.'}
        </div>
      )}

      <div className="space-y-3">
        {filtered.map((row) => {
          const rowHandle = (row.handle || '').toLowerCase()
          const isSelf = viewerHandle && rowHandle === viewerHandle
          return (
            <FollowUserRow
              key={row.person_id}
              row={row}
              isSelf={Boolean(isSelf)}
              busy={Boolean(busyIds[row.person_id])}
              onToggleFollow={(next) => onToggleFollow(row, next)}
            />
          )
        })}
      </div>

      {canLoadMore && (
        <div className="mt-6 flex justify-center">
          <button
            onClick={() => loadPage(page + 1)}
            disabled={loading}
            className="rounded-full border border-white/10 px-4 py-2 text-sm hover:bg-white/5 disabled:opacity-60"
          >
            {loading ? 'Loading…' : 'Load more'}
          </button>
        </div>
      )}
    </div>
  )
}
